import React, { useState, useEffect } from "react";
import "./Settings.css";
import apiService from "../services/apiService";

const createSettingsPayload = (settings = {}) => ({
  apiConfiguration: {
    sfdcConsumerKey: settings?.apiConfiguration?.sfdcConsumerKey || "",
    sfdcConsumerSecret: settings?.apiConfiguration?.sfdcConsumerSecret || "",
    masterSubscriptionEmail: settings?.apiConfiguration?.masterSubscriptionEmail || "",
  },
});

const Settings = ({ currentUser, onClose }) => {
  const [data, setData] = useState(createSettingsPayload());
  const [initialData, setInitialData] = useState(createSettingsPayload());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [saveInfo, setSaveInfo] = useState("");
  const [saveSuccess, setSaveSuccess] = useState("");
  const [adminUsers, setAdminUsers] = useState([]);
  const [adminsLoading, setAdminsLoading] = useState(true);
  const [adminsError, setAdminsError] = useState(null);
  const [adminActionLoading, setAdminActionLoading] = useState("");
  const [showAdminPicker, setShowAdminPicker] = useState(false);
  const [outlookUsers, setOutlookUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [usersError, setUsersError] = useState(null);
  const [adminSearchTerm, setAdminSearchTerm] = useState("");
  const [pendingAdminRemoval, setPendingAdminRemoval] = useState(null);

  const isAdmin = currentUser?.role === "admin";
  const currentUserEmail = String(currentUser?.email || "").toLowerCase();

  useEffect(() => {
    fetchSettings();
    fetchAdminUsers();
  }, []);

  const hasUnsavedChanges =
    data.apiConfiguration.sfdcConsumerKey !== initialData.apiConfiguration.sfdcConsumerKey ||
    data.apiConfiguration.sfdcConsumerSecret !== initialData.apiConfiguration.sfdcConsumerSecret ||
    data.apiConfiguration.masterSubscriptionEmail !== initialData.apiConfiguration.masterSubscriptionEmail;

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const settings = await apiService.getUserSettings();
      const nextSettings = createSettingsPayload(settings);
      setData(nextSettings);
      setInitialData(nextSettings);
    } catch (err) {
      console.error("Failed to fetch settings:", err);
      setError("Failed to load settings. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const fetchAdminUsers = async () => {
    try {
      setAdminsLoading(true);
      setAdminsError(null);
      const response = await apiService.getAdminUsers();
      setAdminUsers(Array.isArray(response?.admins) ? response.admins : []);
    } catch (err) {
      console.error("Failed to fetch admin users:", err);
      setAdminsError("Failed to load admins.");
    } finally {
      setAdminsLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    if (!isAdmin) return;
    setSaveInfo("");
    setSaveSuccess("");
    setData((prev) => ({
      ...prev,
      apiConfiguration: {
        ...prev.apiConfiguration,
        [field]: value,
      },
    }));
  };

  const handleSave = async () => {
    if (!isAdmin) return;
    if (!hasUnsavedChanges) {
      setError(null);
      setSaveSuccess("");
      setSaveInfo("No changes saved.");
      return;
    }
    try {
      setSaving(true);
      setError(null);
      setSaveSuccess("");
      setSaveInfo("");
      await apiService.saveUserSettings(currentUser.email, data);
      setInitialData(data);
      setSaveSuccess("Settings saved successfully.");
    } catch (err) {
      console.error("Failed to save settings:", err);
      setError(err.message || "Failed to save settings.");
    } finally {
      setSaving(false);
    }
  };

  const fetchOutlookUsers = async () => {
    if (!isAdmin || !currentUser?.email) return;

    try {
      setUsersLoading(true);
      setUsersError(null);
      const response = await apiService.getOutlookUsers(currentUser.email);
      setOutlookUsers(Array.isArray(response?.users) ? response.users : []);
    } catch (err) {
      console.error("Failed to fetch Outlook users:", err);
      setUsersError(err.message || "Failed to load users.");
    } finally {
      setUsersLoading(false);
    }
  };

  const openAdminPicker = async () => {
    setShowAdminPicker(true);
    setAdminSearchTerm("");
    await fetchOutlookUsers();
  };

  const closeAdminPicker = () => {
    setShowAdminPicker(false);
    setAdminSearchTerm("");
    setUsersError(null);
  };

  const handleAddAdmin = async (targetUser) => {
    if (!isAdmin || !currentUser?.email || !targetUser?.email) return;

    try {
      setAdminActionLoading(targetUser.email);
      setAdminsError(null);
      const response = await apiService.updateAdminRole(currentUser.email, {
        targetEmail: targetUser.email,
        targetName: targetUser.name,
        role: "admin",
      });
      setAdminUsers(Array.isArray(response?.admins) ? response.admins : []);
      setShowAdminPicker(false);
      setAdminSearchTerm("");
    } catch (err) {
      console.error("Failed to add admin:", err);
      setUsersError(err.message || "Failed to add admin.");
    } finally {
      setAdminActionLoading("");
    }
  };

  const handleRemoveAdmin = async (targetEmail) => {
    if (!isAdmin || !currentUser?.email || !targetEmail) return;
    if (String(targetEmail).toLowerCase() === currentUserEmail) {
      setAdminsError("You can't remove your own admin access.");
      return false;
    }

    try {
      setAdminActionLoading(targetEmail);
      setAdminsError(null);
      const response = await apiService.updateAdminRole(currentUser.email, {
        targetEmail,
        role: "user",
      });
      setAdminUsers(Array.isArray(response?.admins) ? response.admins : []);
      return true;
    } catch (err) {
      console.error("Failed to remove admin:", err);
      setAdminsError(err.message || "Failed to remove admin.");
      return false;
    } finally {
      setAdminActionLoading("");
    }
  };

  const openRemoveAdminConfirmation = (adminUser) => {
    if (!isAdmin || !adminUser?.email || adminActionLoading) return;
    if (String(adminUser.email).toLowerCase() === currentUserEmail) {
      setAdminsError("You can't remove your own admin access.");
      return;
    }

    setAdminsError(null);
    setPendingAdminRemoval({
      email: adminUser.email,
      name: adminUser.name || "",
    });
  };

  const closeRemoveAdminConfirmation = () => {
    if (adminActionLoading) return;
    setPendingAdminRemoval(null);
  };

  const confirmRemoveAdmin = async () => {
    if (!pendingAdminRemoval?.email) return;

    const wasRemoved = await handleRemoveAdmin(pendingAdminRemoval.email);
    if (wasRemoved) {
      setPendingAdminRemoval(null);
    }
  };

  const adminEmailSet = new Set(adminUsers.map((adminUser) => String(adminUser.email || "").toLowerCase()));
  const normalizedSearchTerm = adminSearchTerm.trim().toLowerCase();
  const availableOutlookUsers = outlookUsers.filter((user) => {
    const email = String(user?.email || "").toLowerCase();
    return Boolean(email) && !adminEmailSet.has(email);
  });
  const filteredOutlookUsers = availableOutlookUsers.filter((user) => {
    if (!normalizedSearchTerm) return true;

    const name = String(user?.name || "").toLowerCase();
    const email = String(user?.email || "").toLowerCase();
    return name.includes(normalizedSearchTerm) || email.includes(normalizedSearchTerm);
  });

  return (
    <div className="settings-screen slide-up">
      <div className="settings-screen-inner">
        <div className="settings-panel">
          <div className="settings-header">
            <div className="settings-title-block">
              <div className="settings-title-row">
                <svg className="settings-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="3" />
                  <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
                </svg>
                <h2>Settings</h2>
              </div>
            </div>
            <button className="settings-close-btn" onClick={onClose} aria-label="Close settings">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>

          <div className="settings-content">
            {loading ? (
              <div className="settings-loading">
                <span className="spinner" /> Loading settings...
              </div>
            ) : (
              <>
                {error && <div className="settings-error-banner">{error}</div>}
                {saveInfo && <div className="settings-info-banner">{saveInfo}</div>}
                {saveSuccess && <div className="settings-success-banner">{saveSuccess}</div>}

                <section className="settings-section">
                  <h3 className="section-subtitle">API Configuration</h3>

                  <div className="settings-fields">
                    <div className="input-group">
                      <label>SFDC Consumer Key</label>
                      <div className="apiKey-input-wrapper">
                        {isAdmin ? (
                          <textarea
                            rows={3}
                            value={data.apiConfiguration.sfdcConsumerKey}
                            onChange={(e) => handleInputChange("sfdcConsumerKey", e.target.value)}
                            placeholder="Enter Consumer Key"
                          />
                        ) : (
                          <input
                            type="password"
                            value={data.apiConfiguration.sfdcConsumerKey}
                            placeholder="Enter Consumer Key"
                            disabled
                          />
                        )}
                      </div>
                    </div>

                    <div className="input-group">
                      <label>SFDC Consumer Secret</label>
                      <div className="apiKey-input-wrapper">
                        {isAdmin ? (
                          <textarea
                            rows={3}
                            value={data.apiConfiguration.sfdcConsumerSecret}
                            onChange={(e) => handleInputChange("sfdcConsumerSecret", e.target.value)}
                            placeholder="Enter Consumer Secret"
                          />
                        ) : (
                          <input
                            type="password"
                            value={data.apiConfiguration.sfdcConsumerSecret}
                            placeholder="Enter Consumer Secret"
                            disabled
                          />
                        )}
                      </div>
                    </div>

                    <div className="input-group">
                      <label>Master Subscription Email</label>
                      <div className="apiKey-input-wrapper">
                        <input
                          type="email"
                          value={data.apiConfiguration.masterSubscriptionEmail}
                          placeholder="Enter master subscription email"
                          disabled
                          readOnly
                        />
                      </div>
                    </div>
                  </div>
                </section>

                {isAdmin && (
                  <div className="settings-actions">
                    <button className="save-settings-btn" onClick={handleSave} disabled={saving}>
                      {saving ? "Saving..." : "Save Settings"}
                    </button>
                  </div>
                )}

                <section className="settings-section">
                  <h3 className="section-subtitle">Admins</h3>

                  {adminsError && <div className="settings-error-banner settings-error-banner--compact">{adminsError}</div>}

                  {adminsLoading ? (
                    <div className="settings-inline-state">Loading admins...</div>
                  ) : adminUsers.length > 0 ? (
                    <div className="admin-list">
                      {adminUsers.map((adminUser) => {
                        const isCurrentAdminUser = String(adminUser.email || "").toLowerCase() === currentUserEmail;

                        return (
                          <div key={adminUser.email} className="admin-row">
                            <div className="admin-user-meta">
                              <div className="admin-user-name-row">
                                <span className="admin-user-name">{adminUser.name || adminUser.email}</span>
                                {isCurrentAdminUser && <span className="admin-current-user-badge">You</span>}
                              </div>
                              <span className="admin-user-email">{adminUser.email}</span>
                            </div>

                            {isAdmin && (
                              <button
                                type="button"
                                className="admin-remove-icon-btn"
                                onClick={() => openRemoveAdminConfirmation(adminUser)}
                                disabled={Boolean(adminActionLoading) || isCurrentAdminUser}
                                aria-label={
                                  isCurrentAdminUser
                                    ? "You cannot remove your own admin access"
                                    : `Remove ${adminUser.email} from admins`
                                }
                                title={
                                  isCurrentAdminUser
                                    ? "You cannot remove your own admin access"
                                    : adminActionLoading === adminUser.email
                                      ? "Removing..."
                                      : "Remove admin"
                                }
                              >
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <polyline points="3 6 5 6 21 6" />
                                  <path d="M19 6l-1 14H6L5 6" />
                                  <path d="M10 11v6" />
                                  <path d="M14 11v6" />
                                  <path d="M9 6V4h6v2" />
                                </svg>
                              </button>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="settings-inline-state">No admins found.</div>
                  )}

                  {isAdmin && (
                    <button
                      type="button"
                      className="admin-open-picker-btn"
                      onClick={openAdminPicker}
                      disabled={!currentUser?.email}
                    >
                      Add Admin
                    </button>
                  )}
                </section>
              </>
            )}
          </div>
        </div>
      </div>

      {showAdminPicker && (
        <div className="admin-picker-overlay" onClick={closeAdminPicker}>
          <div className="admin-picker-modal" onClick={(event) => event.stopPropagation()}>
            <div className="admin-picker-header">
              <h3>Add Admin</h3>
              <button type="button" className="admin-picker-close-btn" onClick={closeAdminPicker} aria-label="Close add admin popup">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>

            <input
              className="admin-picker-search"
              type="text"
              value={adminSearchTerm}
              onChange={(event) => setAdminSearchTerm(event.target.value)}
              placeholder="Search by name or email"
            />

            {usersError && <div className="settings-error-banner settings-error-banner--compact">{usersError}</div>}

            {usersLoading ? (
              <div className="settings-inline-state">Loading users...</div>
            ) : filteredOutlookUsers.length > 0 ? (
              <div className="admin-picker-list">
                {filteredOutlookUsers.map((user) => {
                  const isAddingThisUser = adminActionLoading === user.email;

                  return (
                    <div key={user.email} className="admin-picker-row">
                      <div className="admin-picker-user">
                        <span className="admin-user-name">{user.name || user.email}</span>
                        <span className="admin-user-email">{user.email}</span>
                      </div>

                      <button
                        type="button"
                        className="admin-add-btn"
                        onClick={() => handleAddAdmin(user)}
                        disabled={Boolean(adminActionLoading)}
                      >
                        {isAddingThisUser ? "Adding..." : "Add"}
                      </button>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="settings-inline-state">
                {normalizedSearchTerm ? "No matching users found." : "No users available to add."}
              </div>
            )}
          </div>
        </div>
      )}

      {pendingAdminRemoval && (
        <div className="admin-confirm-overlay" onClick={closeRemoveAdminConfirmation}>
          <div
            className="admin-confirm-modal"
            onClick={(event) => event.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="remove-admin-title"
            aria-describedby="remove-admin-description"
          >
            <div className="admin-confirm-header">
              <h3 id="remove-admin-title">Remove admin?</h3>
            </div>

            <div className="admin-confirm-body">
              <p id="remove-admin-description" className="admin-confirm-text">
                Are you sure you want to remove{" "}
                <strong>{pendingAdminRemoval.name || pendingAdminRemoval.email}</strong>
                {pendingAdminRemoval.name ? ` (${pendingAdminRemoval.email})` : ""} from the admin list?
              </p>
              <p className="admin-confirm-subtext">This user will lose access to admin settings and admin-only actions.</p>
            </div>

            <div className="admin-confirm-actions">
              <button
                type="button"
                className="admin-confirm-btn admin-confirm-btn--secondary"
                onClick={closeRemoveAdminConfirmation}
                disabled={Boolean(adminActionLoading)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="admin-confirm-btn admin-confirm-btn--danger"
                onClick={confirmRemoveAdmin}
                disabled={Boolean(adminActionLoading)}
              >
                {adminActionLoading === pendingAdminRemoval.email ? "Removing..." : "Remove"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Settings;
